import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ExportBriefingButton from "@/components/ExportBriefingButton";
import { Trash2, Eye, Download, PenLine } from "lucide-react";
import { bandColor } from "@/lib/forms";

function StateTag({ s }) {
  const draft = s.status === "draft";
  return <span data-testid={`record-state-${s.id}`} className={`ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded font-semibold ${draft ? "bg-slate-100 text-slate-700" : "bg-emerald-100 text-emerald-800"}`}>{draft ? "Private draft" : "Submitted"}</span>;
}

function Score({ s }) {
  if (!s.score) return <span className="text-xs text-slate-400">n/a</span>;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${bandColor(s.score.percent)}`}>{s.score.percent}% · {s.score.band}</span>;
}

function RowActions({ s, userId, remove }) {
  return (
    <div className="flex gap-1 justify-end">
      {s.status === "draft" && s.submitted_by === userId && (
        <Link to={`/app/forms/fill/${s.template_id}?draft=${s.id}`}><Button size="sm" variant="outline" className="h-8" data-testid={`continue-draft-${s.id}`}><PenLine className="w-4 h-4 mr-1" aria-hidden="true" /> Continue</Button></Link>
      )}
      <Link to={`/app/forms/submissions/${s.id}`}><Button size="sm" variant="ghost" className="h-8" data-testid={`view-sub-${s.id}`} aria-label="View record"><Eye className="w-4 h-4" aria-hidden="true" /></Button></Link>
      <Link to={`/app/forms/submissions/${s.id}`}><Button size="sm" variant="ghost" className="h-8" aria-label="Open record to download PDF"><Download className="w-4 h-4" aria-hidden="true" /></Button></Link>
      <Button size="sm" variant="ghost" data-testid={`delete-sub-${s.id}`} onClick={() => remove(s.id)} className="h-8 text-rose-600" aria-label="Delete record"><Trash2 className="w-4 h-4" aria-hidden="true" /></Button>
    </div>
  );
}

// Existing "Submissions" tab, preserved: private drafts + submitted records, PDF export
export default function MyRecords({ subs, selected, setSelected, remove, userId }) {
  const formsPayload = useMemo(() => ({ include_highlights: false, include_forms: true, submission_ids: selected.length ? selected : null }), [selected]);
  const toggle = (id, v) => setSelected(v ? [...selected, id] : selected.filter((x) => x !== id));
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="text-sm text-slate-600" data-testid="selected-count">{selected.length ? `${selected.length} selected` : "Private drafts are visible only to you. Select rows to export specific records."}</div>
        <ExportBriefingButton testid="export-forms-btn" filePrefix="forms" label={selected.length ? `Export ${selected.length} selected as PDF` : "Export my forms as PDF"} payload={formsPayload} />
      </div>
      <div className="sm:hidden space-y-3" data-testid="submissions-cards">
        {subs.map((s) => (
          <Card key={s.id} data-testid={`submission-card-${s.id}`} className="p-4 bg-white border-slate-200">
            <div className="flex items-start gap-3">
              <Checkbox data-testid={`select-sub-mobile-${s.id}`} checked={selected.includes(s.id)} onCheckedChange={(v) => toggle(s.id, v)} className="mt-1" aria-label={`Select ${s.template_title}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#0A2A5E]">{s.template_title}<StateTag s={s} /></div>
                <div className="text-xs text-slate-500 mt-0.5">{new Date(s.created_at).toLocaleDateString()} · {s.subject_name || "—"} · by {s.submitted_by_name}</div>
                <div className="mt-2 flex items-center justify-between gap-2"><Score s={s} /><RowActions s={s} userId={userId} remove={remove} /></div>
              </div>
            </div>
          </Card>
        ))}
        {subs.length === 0 && <Card className="p-8 text-center text-slate-500 bg-white border-slate-200">No records yet.</Card>}
      </div>
      <Card className="hidden sm:block bg-white border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-3"><Checkbox data-testid="select-all-subs" aria-label="Select all" checked={subs.length > 0 && selected.length === subs.length} onCheckedChange={(v) => setSelected(v ? subs.map((s) => s.id) : [])} /></th>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Form</th><th className="px-4 py-3">Subject</th><th className="px-4 py-3">Completed by</th><th className="px-4 py-3">Score</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} data-testid={`submission-row-${s.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-3"><Checkbox data-testid={`select-sub-${s.id}`} aria-label={`Select ${s.template_title}`} checked={selected.includes(s.id)} onCheckedChange={(v) => toggle(s.id, v)} /></td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 font-medium text-[#0A2A5E]">{s.template_title}<StateTag s={s} /></td>
                  <td className="px-4 py-3">{s.subject_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{s.submitted_by_name}</td>
                  <td className="px-4 py-3"><Score s={s} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap"><RowActions s={s} userId={userId} remove={remove} /></td>
                </tr>
              ))}
              {subs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No records yet. Start a form to get going.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
